const SESSION_KEY = "wsCustomerAvatarUrls";
const NO_AVATAR = "__none__";
const NEGATIVE_CACHE_MS = 5 * 60 * 1000;

const memoryCache = new Map();
const revisionById = new Map();
const listeners = new Set();

function readSessionMap() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSessionEntry(customerId, value) {
  try {
    const map = readSessionMap();
    map[customerId] = value;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(map));
  } catch {
    // sessionStorage may be unavailable or full — memory cache still applies.
  }
}

function removeSessionEntry(customerId) {
  try {
    const map = readSessionMap();
    delete map[customerId];
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

export function getCustomerAvatarCacheRevision(customerId) {
  if (!customerId) return 0;
  return revisionById.get(customerId) || 0;
}

export function subscribeCustomerAvatarCache(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyCustomerAvatarCacheChanged(customerId) {
  if (!customerId) return;
  const next = (revisionById.get(customerId) || 0) + 1;
  revisionById.set(customerId, next);
  listeners.forEach((fn) => fn(customerId));
}

/** Clear storage without notifying — used before refresh repopulates cache. */
export function clearCustomerAvatarCacheEntry(customerId) {
  if (!customerId) return;
  memoryCache.delete(customerId);
  removeSessionEntry(customerId);
}

function clearCustomerAvatarCache(customerId, { notify = true } = {}) {
  clearCustomerAvatarCacheEntry(customerId);
  if (notify) notifyCustomerAvatarCacheChanged(customerId);
}

/**
 * Read a cached avatar image URL for a customer (memory, then sessionStorage).
 * Returns `undefined` when not cached, `null` when cached as "no avatar".
 */
export function getCachedCustomerAvatarUrl(customerId) {
  if (!customerId) return undefined;
  const mem = memoryCache.get(customerId);
  if (mem) {
    if (mem.expiresAt && mem.expiresAt < Date.now()) {
      memoryCache.delete(customerId);
    } else {
      return mem.url;
    }
  }
  const sessionValue = readSessionMap()[customerId];
  if (sessionValue === undefined) return undefined;
  const url = sessionValue === NO_AVATAR ? null : sessionValue;
  memoryCache.set(customerId, { url });
  return url;
}

/** Store a resolved avatar URL (or null when customer has no avatar). */
export function setCachedCustomerAvatarUrl(customerId, url) {
  if (!customerId) return;
  const entry = url
    ? { url }
    : { url: null, expiresAt: Date.now() + NEGATIVE_CACHE_MS };
  memoryCache.set(customerId, entry);
  writeSessionEntry(customerId, url || NO_AVATAR);
}

/** Clear cached URL so the next view fetches fresh data. */
export function invalidateCustomerAvatarCache(customerId) {
  clearCustomerAvatarCache(customerId, { notify: true });
}

export function bumpCustomerAvatarCacheRevision(customerId) {
  notifyCustomerAvatarCacheChanged(customerId);
}
